import React, { useCallback, useEffect, useState } from "react";
import { adminApi, resolveImage } from "../../lib/admin-api";
import { Plus, X, Edit2, Trash2, Sparkles, Upload, RefreshCw, Eye, EyeOff } from "lucide-react";

const CATEGORIES = ["stoel", "loungefauteuil", "verlichting", "tafel", "bank"];

const emptyForm = {
  name: "",
  designer: "",
  brand: "",
  year: "",
  category: "stoel",
  short_nl: "",
  short_en: "",
  long_nl: "",
  long_en: "",
  image_url: "",
  visible: true,
  featured: false,
};

export default function AdminClassics() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | <slug>
  const limit = 30;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/classics", { params: { q, page, limit } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [q, page]);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (it) => {
    await adminApi.patch(`/classics/${it.slug}`, { visible: !it.visible });
    load();
  };

  const remove = async (it) => {
    if (!window.confirm(`Verwijderen: ${it.name}?`)) return;
    await adminApi.delete(`/classics/${it.slug}`);
    load();
  };

  return (
    <div data-testid="admin-classics" className="max-w-6xl">
      <div className="flex items-end justify-between mb-10 gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-stoel-walnut mb-3">— Inhoud</div>
          <h1 className="stoel-display text-4xl">Klassiekers</h1>
        </div>
        <button
          data-testid="classics-add-new"
          onClick={() => setEditing("new")}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta transition-colors"
        >
          <Plus size={16} /> Nieuw stuk
        </button>
      </div>

      <div className="mb-6 flex gap-3 items-center">
        <input
          data-testid="classics-search"
          value={q}
          onChange={(e) => { setPage(1); setQ(e.target.value); }}
          placeholder="Zoek op naam, ontwerper, producent…"
          className="flex-1 max-w-md bg-stoel-paper border border-stoel-line px-3 py-2 text-sm focus:outline-none focus:border-stoel-ink"
        />
        <span data-testid="classics-total" className="text-xs uppercase tracking-[0.2em] text-stoel-inkSoft">
          {total} {total === 1 ? "stuk" : "stukken"}
        </span>
      </div>

      <div className="bg-stoel-paper border border-stoel-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-stoel-line bg-stoel-paperDeep/60">
            <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-stoel-walnut">
              <th className="px-3 py-2 w-12"></th>
              <th className="px-3 py-2">Naam</th>
              <th className="px-3 py-2">Ontwerper</th>
              <th className="px-3 py-2">Producent</th>
              <th className="px-3 py-2 w-16">Jaar</th>
              <th className="px-3 py-2 w-32">Categorie</th>
              <th className="px-3 py-2 w-24">Zichtbaar</th>
              <th className="px-3 py-2 w-24 text-right">Acties</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="8" className="px-3 py-8 text-center text-stoel-inkSoft">Laden…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan="8" className="px-3 py-8 text-center text-stoel-inkSoft">Geen resultaten.</td></tr>
            ) : items.map((it) => (
              <tr key={it.slug} data-testid={`row-${it.slug}`} className="border-b border-stoel-line/60 hover:bg-stoel-paperDeep/40">
                <td className="px-3 py-2">
                  <img src={resolveImage(it.image_url)} alt="" className="w-9 h-9 object-cover bg-stoel-paperDeep" loading="lazy" />
                </td>
                <td className="px-3 py-2 text-stoel-ink">
                  <div className="flex items-center gap-2">
                    {it.name}
                    {it.featured && <span className="text-[10px] uppercase tracking-[0.2em] text-stoel-terracotta">Selectie</span>}
                  </div>
                </td>
                <td className="px-3 py-2 text-stoel-inkSoft">{it.designer}</td>
                <td className="px-3 py-2 text-stoel-inkSoft">{it.brand}</td>
                <td className="px-3 py-2 text-stoel-inkSoft tabular-nums">{it.year || "—"}</td>
                <td className="px-3 py-2 text-stoel-inkSoft">{it.category}</td>
                <td className="px-3 py-2">
                  <button
                    data-testid={`toggle-visible-${it.slug}`}
                    onClick={() => toggleVisible(it)}
                    className={`inline-flex items-center gap-1 text-xs ${it.visible ? "text-stoel-ink" : "text-stoel-inkSoft/60"}`}
                  >
                    {it.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                    {it.visible ? "Ja" : "Nee"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button data-testid={`edit-${it.slug}`} onClick={() => setEditing(it.slug)} className="p-1 hover:text-stoel-terracotta" title="Bewerken"><Edit2 size={14} /></button>
                  <button data-testid={`delete-${it.slug}`} onClick={() => remove(it)} className="p-1 hover:text-stoel-terracotta ml-2" title="Verwijderen"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="mt-4 flex items-center justify-between text-xs text-stoel-inkSoft">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="disabled:opacity-30 hover:text-stoel-ink">← Vorige</button>
          <span>Pagina {page} / {Math.ceil(total / limit)}</span>
          <button disabled={page * limit >= total} onClick={() => setPage(page + 1)} className="disabled:opacity-30 hover:text-stoel-ink">Volgende →</button>
        </div>
      )}

      {editing && (
        <ClassicEditDrawer
          slug={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function ClassicEditDrawer({ slug, onClose, onSaved }) {
  const isNew = !slug;
  const [form, setForm] = useState(emptyForm);
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const r = await adminApi.get("/classics", { params: { q: slug } });
        const found = (r.data.items || []).find((x) => x.slug === slug);
        if (found) setForm({ ...emptyForm, ...found, year: found.year || "" });
        setLoaded(true);
      } catch (e) {
        setError("Kon stuk niet laden");
        setLoaded(true);
      }
    })();
  }, [slug, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const aiFill = async () => {
    if (!form.name.trim()) {
      setError("Vul eerst een naam in.");
      return;
    }
    setError("");
    setAiBusy(true);
    try {
      const r = await adminApi.post("/ai-fill", { name: form.name.trim() });
      const filled = r.data;
      // Don't overwrite fields the user has already filled
      setForm((f) => ({
        ...f,
        designer: f.designer || filled.designer || "",
        brand: f.brand || filled.brand || "",
        year: f.year || filled.year || "",
        category: f.category && f.category !== "stoel" ? f.category : (filled.category || "stoel"),
        short_nl: f.short_nl || filled.short_nl || "",
        short_en: f.short_en || filled.short_en || "",
        long_nl: f.long_nl || filled.long_nl || "",
        long_en: f.long_en || filled.long_en || "",
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || "AI-vulling mislukt");
    } finally {
      setAiBusy(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminApi.post("/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", r.data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload mislukt");
    } finally {
      setImgBusy(false);
    }
  };

  const regenImage = async () => {
    if (!form.name.trim()) {
      setError("Naam is verplicht voor AI-beeld.");
      return;
    }
    setImgBusy(true);
    setError("");
    try {
      const ctx = `${form.name} by ${form.designer || "an unspecified designer"}, ${form.year || "mid-century"}, category ${form.category}`;
      const r = await adminApi.post("/images/generate", {
        prompt_context: ctx,
        kind: "product",
        out_slug: slug ? `product_${slug}_${Date.now()}` : undefined,
      });
      set("image_url", r.data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Beeld genereren mislukt");
    } finally {
      setImgBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, year: form.year ? parseInt(form.year, 10) : null };
      if (isNew) {
        await adminApi.post("/classics", payload);
      } else {
        await adminApi.patch(`/classics/${slug}`, payload);
      }
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="classic-edit-drawer" className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-stoel-ink/50" onClick={onClose} />
      <div className="w-full max-w-2xl bg-stoel-paper overflow-y-auto p-8 md:p-10 shadow-2xl">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="stoel-eyebrow mb-2">{isNew ? "Nieuw" : "Bewerken"}</div>
            <h2 className="stoel-display text-3xl">{form.name || (isNew ? "Nieuw stuk" : "—")}</h2>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="text-stoel-inkSoft hover:text-stoel-ink"><X /></button>
        </div>

        {!loaded ? (
          <div className="text-stoel-inkSoft animate-pulse">Laden…</div>
        ) : (
          <div className="space-y-5">
            <Field label="Naam" testid="form-name">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="input-name" />
            </Field>

            <div className="flex flex-wrap gap-3 items-center bg-stoel-paperDeep/60 border border-stoel-line p-4">
              <Sparkles size={16} className="text-stoel-terracotta" />
              <span className="text-sm text-stoel-ink">
                AI-vulling: laat Claude alle lege velden invullen op basis van de naam.
              </span>
              <button
                data-testid="ai-fill-btn"
                onClick={aiFill}
                disabled={aiBusy || !form.name.trim()}
                className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-stoel-ink text-stoel-paper disabled:opacity-50 hover:bg-stoel-terracotta"
              >
                {aiBusy ? "Bezig…" : "AI-vullen"}
              </button>
            </div>
            <p className="text-xs text-stoel-walnut italic">Reviewed by you — controleer wat de AI invult.</p>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Ontwerper"><input data-testid="input-designer" value={form.designer} onChange={(e) => set("designer", e.target.value)} className={inputCls} /></Field>
              <Field label="Producent"><input data-testid="input-brand" value={form.brand} onChange={(e) => set("brand", e.target.value)} className={inputCls} /></Field>
              <Field label="Jaar"><input data-testid="input-year" type="number" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} /></Field>
              <Field label="Categorie">
                <select data-testid="input-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Short NL"><textarea data-testid="input-short-nl" rows={2} value={form.short_nl} onChange={(e) => set("short_nl", e.target.value)} className={inputCls} /></Field>
            <Field label="Short EN"><textarea data-testid="input-short-en" rows={2} value={form.short_en} onChange={(e) => set("short_en", e.target.value)} className={inputCls} /></Field>
            <Field label="Long NL"><textarea data-testid="input-long-nl" rows={4} value={form.long_nl} onChange={(e) => set("long_nl", e.target.value)} className={inputCls} /></Field>
            <Field label="Long EN"><textarea data-testid="input-long-en" rows={4} value={form.long_en} onChange={(e) => set("long_en", e.target.value)} className={inputCls} /></Field>

            <Field label="Beeld">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {form.image_url && (
                    <img src={resolveImage(form.image_url)} alt="" className="w-24 h-24 object-cover bg-stoel-paperDeep border border-stoel-line" />
                  )}
                  <div className="flex flex-col gap-2 flex-1">
                    <label className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-ink text-sm cursor-pointer hover:bg-stoel-ink hover:text-stoel-paper">
                      <Upload size={14} /> Upload
                      <input data-testid="upload-input" type="file" accept="image/*" hidden onChange={uploadImage} />
                    </label>
                    <button data-testid="regen-image" onClick={regenImage} disabled={imgBusy} className="inline-flex items-center gap-2 px-3 py-2 border border-stoel-line text-sm hover:bg-stoel-paperDeep disabled:opacity-50">
                      <RefreshCw size={14} /> {imgBusy ? "Bezig…" : "Regenereer AI"}
                    </button>
                  </div>
                </div>
                <input data-testid="input-image-url" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className={inputCls} placeholder="/api/static/..." />
              </div>
            </Field>

            <div className="flex items-center gap-6">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input data-testid="input-visible" type="checkbox" checked={form.visible} onChange={(e) => set("visible", e.target.checked)} />
                <span className="text-sm">Zichtbaar</span>
              </label>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input data-testid="input-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
                <span className="text-sm">Selectie (featured)</span>
              </label>
            </div>

            {error && (
              <div data-testid="drawer-error" className="bg-stoel-terracotta/10 border-l-2 border-stoel-terracotta text-stoel-ink p-3 text-sm">{error}</div>
            )}

            <div className="pt-4 flex gap-3">
              <button data-testid="save-btn" onClick={save} disabled={saving || !form.name.trim()} className="px-6 py-2.5 bg-stoel-ink text-stoel-paper text-sm hover:bg-stoel-terracotta disabled:opacity-50">
                {saving ? "Opslaan…" : "Opslaan"}
              </button>
              <button onClick={onClose} className="px-5 py-2.5 border border-stoel-line text-sm hover:bg-stoel-paperDeep">Annuleren</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full bg-stoel-paper border border-stoel-line px-3 py-2 text-sm focus:outline-none focus:border-stoel-ink";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-stoel-walnut mb-1.5">{label}</span>
      {children}
    </label>
  );
}
